<template>
  <InfuseSlideInLayout
    :title="role?.name"
    :subtitle="role?.description"
    :parent-to="parentTo"
    :editable="true"
    @edit="showUpdateRoleModal = true"
  >
    <AssetLevelOverview :asset="role" />

    <InfuseRouterTabLayout v-if="role" :model="role" :is-loading="isLoading" />

    <!-- UPDATE -->
    <MutationRoleModal
      v-if="showUpdateRoleModal"
      :role="role"
      :loading="isUpdatingRole"
      @close="closeUpdateModal"
      @mutate="onUpdateRole"
    />
  </InfuseSlideInLayout>
</template>

<script setup lang="ts">
import { useRoute } from 'vue-router';

import { AssetLevelOverview } from '@infuse/iam/components';
import { useRouteLevel } from '@infuse/iam/composables';
import { useUpdateRole } from '@infuse/iam/role/mutations';
import { useRole } from '@infuse/iam/role/queries';
import { UpdateRole } from '@infuse/iam/role/type';
import { MutationRoleModal } from '@infuse/iam/role/ui';
import { UserLevel } from '@infuse/iam/user/type';
import { InfuseRouterTabLayout, InfuseSlideInLayout } from '@infuse/ui/layouts';
import { permissionOrRedirect } from '@infuse/ui/utils';
import { computed, ref, watch } from 'vue';

const route = useRoute();

const { role, isLoading, fetchRoleId } = useRole();

const { level } = useRouteLevel();

const parentTo = computed(() => {
  if (level.value === UserLevel.TENANT) {
    return { name: 'tenant.roles' };
  } else if (level.value === UserLevel.ORGANISATION) {
    return { name: 'organisation.roles' };
  }

  return { name: 'role' };
});

const showUpdateRoleModal = ref(false);

const { mutateAsync: updateRole, isPending: isUpdatingRole } = useUpdateRole();

permissionOrRedirect();

watch(
  () => route.params.roleId || route.params.id,
  () => {
    fetchRoleId.value = `${route.params.roleId || route.params.id}`;
  },
  { immediate: true }
);

async function onUpdateRole(vars: { id: string; role: UpdateRole }): Promise<void> {
  await updateRole({ roleId: vars.id, role: vars.role });
  closeUpdateModal();
}

function closeUpdateModal() {
  showUpdateRoleModal.value = false;
}
</script>
